import { scrollIntoViewRespectingMotion } from './scroll';

/**
 * Aus einem frisch angelegten Produkt wird eine PO-Zeile.
 *
 * PR2 PO-Schnellweg (2026-10-27). Die Bestellung legt neue Produkte seitdem über
 * den Basismodus des Assistenten an (`UnifiedCreateWizard mode="basic"
 * sourceContext="po"`), und der gibt seine Daten FLACH zurück:
 * `{sku, product_id, product_name, variant_name, cost_per_item, moq, …}`.
 * Der volle Assistent lieferte `{local_product: {…}}`. Bis hierher las der
 * PO-Handler NUR die zweite Form — ein im Schnellweg angelegtes Produkt wäre
 * NICHT in die Bestellung gekommen (Plan, Befund „Rückgabeform passt nicht“).
 *
 * Beide Formen werden verstanden; die zweite bleibt, weil sie nichts kostet und
 * ein Rückbau auf den vollen Assistenten sonst still brechen würde.
 *
 * 🔴 `sku` ist Pflicht, `productId` nicht: die PO-Zeile fällt heute schon auf
 * die `sku` zurück (`moqLagen` in `PODetail.jsx`, „productId zuerst, sku als
 * Rueckfall“). Ohne `sku` gibt es KEINE Zeile — eine Zeile ohne Kennung wäre
 * nicht bestellbar und nicht zuzuordnen.
 *
 * 🔴 `productId` ist bei einem frisch angelegten Produkt IMMER `null` (R15 B-1,
 * 27.10.). In der PO heißt `productId` `shp_products.id` — die lokale Zeilen-ID,
 * die `GET /api/products` als `id` liefert (`produktIndex`/`addProduct` in
 * `PODetail.jsx`). Das `product_id` aus `onSuccess` ist dagegen die
 * SHOPIFY-Produkt-ID, ein anderer Schlüsselraum. Eingetragen, wäre sie dauerhaft in
 * `order_items` gelandet: beim Status `Done` prüft `push_layers_from_po`
 * (`backend/stock_layers.py`) gegen `shp_products.id`, die Zeile gälte als
 * verwaist (keine Lagerschicht, Anomalie `stock_layer_missing`), und
 * `price_references` zerfiele auf zwei Schlüssel. Der volle Assistent setzte
 * `local_product.id` ebenfalls `null`; die PO fällt dann wie bisher auf die `sku`
 * zurück. `onSuccess` darf `product_id` weiterreichen — nur nicht hierher.
 *
 * @param {object|undefined} daten  was `onSuccess` des Assistenten liefert
 * @returns {object|null} die neue PO-Zeile, oder `null`
 */
export function zeileAusNeuemProdukt(daten) {
  if (!daten || typeof daten !== 'object') return null;
  const quelle = daten.local_product || daten;
  if (!quelle || !quelle.sku) return null;
  // Nur die lokale ID des vollen Assistenten (`local_product.id`, dort `null`)
  // ist eine `shp_products.id`. Die flache Form trägt keine — siehe oben.
  const id = daten.local_product ? quelle.id : null;
  return {
    productId: id ?? null,
    sku: quelle.sku,
    product_name: quelle.product_name || '',
    variant_name: quelle.variant_name || '',
    moq: Number(quelle.moq) || 0,
    quantity: 1,
    // in der Währung der Bestellung — wie bisher beim vollen Assistenten
    cost_per_item: Number(quelle.cost_per_item) || 0,
    tax_pct: 0,
  };
}

/**
 * Die Fehlermeldung, wenn aus einem angelegten Produkt KEINE Zeile wird (R16 W2).
 *
 * Bis 27.10. schloss der Dialog dann still — obwohl das Produkt in Shopify schon
 * existierte. `zeileAusNeuemProdukt` liefert `null` nur ohne `sku`; die SKU kann
 * diese Meldung deshalb nicht nennen. Sie nennt, was da ist (Name, Shopify-ID),
 * und sagt, was NICHT passiert ist und was der Bediener tun kann.
 *
 * 🔴 R16 Runde 2 H-g: sie verspricht nichts Ungemessenes. Belegt ist nur, WANN
 * die Liste hinter „Add product“ geladen wird: `PODetail.jsx` holt
 * `GET /api/products` in einem `useEffect` ohne Abhaengigkeiten, also einmal beim
 * Oeffnen der Seite — ein Produkt, das danach entsteht, steht dort erst nach
 * einem Neuladen. OB es dann dort steht, ist fuer genau diesen Fall (Antwort ohne
 * SKU) nicht belegt; die Meldung verspricht deshalb keinen Zeitpunkt („after the
 * next sync“). Bis Runde 2 stand hier „once it shows up in the product search“ —
 * als kaeme es von selbst.
 *
 * R16 Runde 3 H-2: die Handlung steht ausdruecklich da. Runde 2 deutete das
 * Neuladen nur an („loaded when this page opens“) — und ein Neuladen verwirft
 * ungespeicherte Aenderungen still, im Anlegemodus die ganze Entwurfs-PO
 * (PODetail hat keinen `beforeunload`-Schutz). Deshalb zuerst „Save this PO“.
 *
 * @param {object|undefined} daten  was `onSuccess` des Assistenten liefert
 * @returns {string}
 */
export function meldungZeileFehlt(daten) {
  const quelle = (daten && (daten.local_product || daten)) || {};
  const name = quelle.product_name ? `“${quelle.product_name}”` : 'The new product';
  const id = daten?.product_id ? ` (Shopify ID ${daten.product_id})` : '';
  return `${name}${id} exists in Shopify, but the PO line could not be created because the response carried no SKU. Save this PO, then reload the page and add it via “Add product” — that list only loads when the page opens.`;
}

/**
 * Die Mengenfelder der Bestellung mit der neuen Zeile (R16 Runde 2 H-e).
 *
 * `PODetail.jsx` haelt die getippten Mengen in einem eigenen Feld
 * (`rowQuantity`) neben `items`, und das Mengenfeld zeigt `rowQuantity[i] || ''`.
 * Die Uebernahme fuegte bis hierher nur in `items` ein: die neue Zeile hatte
 * Menge 1, ihr Feld stand aber LEER — und ein Verlassen des leeren Feldes
 * schreibt beim `onBlur` 0. Seit der Fokus nach der Uebernahme auf genau diesem
 * Feld landet, reichte ein Tab, um die Menge still auf 0 zu setzen. Deshalb
 * bekommt die neue Zeile ihren Eintrag an derselben Stelle wie in `items`.
 *
 * Ist die Liste kuerzer als `idx` (Zeilen aus „Add product“ tragen keinen
 * Eintrag), wird bis `idx` aufgefuellt; die Luecken bleiben leer wie bisher.
 *
 * @param {string[]|undefined} vorher  `rowQuantity` vor der Uebernahme
 * @param {number} idx                 Einfuegestelle (wie in `items`)
 * @param {number} menge               die Menge der neuen Zeile
 * @returns {string[]} eine neue Liste
 */
export function mengenMitNeuerZeile(vorher, idx, menge) {
  const neu = Array.isArray(vorher) ? [...vorher] : [];
  if (neu.length < idx) neu.length = idx;
  neu.splice(idx, 0, String(menge));
  return neu;
}

/** Das Mengenfeld einer Bestellzeile: Tabellenzeile wie Handykarte verweisen es per
 * `aria-describedby` auf ihren MOQ-Hinweis (`moq-hinweis-t-<i>` / `-m-<i>`, O21) —
 * das einzige Eingabefeld der Zeile mit dieser Kennung. */
const MENGENFELD = 'input[aria-describedby^="moq-hinweis-"]';

/**
 * Scrollt die neue Zeile ins Bild (R16 W2): auf einer langen PO haengt sie unten
 * an, ausserhalb des Blicks. Gesucht wird ueber `data-po-sku` (Tabellenzeile und
 * Handykarte in `PODetail.jsx` tragen es). Die Zeile entsteht erst mit dem
 * naechsten Rendern; deshalb wird einige Bilder lang nachgesehen, dann
 * aufgegeben — ohne Fehler, die Rueckmeldung steht schon im Toast.
 *
 * R16 Runde 2 H-e: danach liegt der Fokus im MENGENFELD der neuen Zeile — dem
 * naechsten Handgriff. Bis hierher gab der Dialog ihn beim Schliessen an „Add
 * product“ zurueck, und auf einer langen PO lag er damit ausserhalb des Bildes.
 * Die Rueckgabe durch MUI wird NICHT bekaempft (kein `disableRestoreFocus`): sie
 * laeuft im Commit des Schliessens, die Suche hier erst ein Bild spaeter, und der
 * Fokus wird noch ein Bild danach gesetzt — nach der Rueckgabe, nicht gegen sie.
 * `preventScroll`, damit das Fokussieren das Scrollen auf die Mitte nicht
 * ueberschreibt. Ohne Mengenfeld (Ansicht ohne Bearbeiten) bleibt der Fokus, wo
 * er ist. Der Inhalt des Feldes wird markiert (R16 Runde 3 H-1), damit die
 * erste Taste die Menge 1 ersetzt, statt an sie anzuhaengen.
 *
 * @param {string} sku
 * @param {object} [opts]
 * @param {number} [opts.versuche=20]
 * @param {Function} [opts.scrollen]  Ziel des Scrollens (Proben ersetzen es)
 */
export function zeileInsBild(sku, { versuche = 20, scrollen } = {}) {
  if (!sku || typeof document === 'undefined') return;
  const wert = (typeof CSS !== 'undefined' && CSS.escape) ? CSS.escape(String(sku))
    : String(sku).replace(/["\\]/g, '\\$&');
  const naechstes = (typeof window !== 'undefined' && window.requestAnimationFrame)
    ? (f) => window.requestAnimationFrame(f)
    : (f) => setTimeout(f, 16);
  const ziel = scrollen || ((el) => scrollIntoViewRespectingMotion(el, { block: 'center' }));
  let rest = versuche;
  const suchen = () => {
    const el = document.querySelector(`[data-po-sku="${wert}"]`);
    if (el) {
      ziel(el);
      naechstes(() => {
        const feld = el.querySelector(MENGENFELD);
        if (!feld || typeof feld.focus !== 'function') return;
        feld.focus({ preventScroll: true });
        // R16 Runde 3 H-1: das Feld zeigt „1“, und ohne Auswahl stand der
        // Cursor am Rand — wer „5“ tippte, bekam „15“. Markiert, ersetzt die
        // erste Taste die 1 (das Feld ist `type="text"`, `select()` greift).
        if (typeof feld.select === 'function') feld.select();
      });
      return;
    }
    rest -= 1;
    if (rest > 0) naechstes(suchen);
  };
  naechstes(suchen);
}

export default zeileAusNeuemProdukt;
